import { fileDownload } from 'utils/fileDownload';
import { logError } from 'utils/sentryUtils';

import { mmFetch } from './mmFetch';
import { mmFetchWithResponse } from './mmFetchWithResponse';

const fbmApi = {
    addItemToASN: async (id, item) => {
        const url = `api/fbm/v1/asn/${id}/items`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
        return response;
    },
    addItemToPackagingASN: async (id, item) => {
        const url = `api/fbm/v1/packagingASN/${id}/items`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
        return response;
    },
    cancelASN: async (id, fbmAccount = '') => {
        let url = `api/fbm/v1/asn/${id}/cancel`;
        if (fbmAccount) {
            url += `?fbmAccount=${fbmAccount}`;
        }
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    cancelPackagingASN: async (id, fbmAccount = '') => {
        let url = `api/fbm/v1/packagingASN/${id}/cancel`;
        if (fbmAccount) {
            url += `?fbmAccount=${fbmAccount}`;
        }
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    createPackagingASN: async (item) => {
        const url = `api/fbm/v1/packagingASN`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
        return response;
    },
    createPurchaseOrder: async (item) => {
        const url = `api/fbm/v1/asn`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
        return response;
    },
    downloadIngestResults: async (fbmAccount = '') => {
        let url = `api/fbm/v1/orders/ingest/download`;
        if (fbmAccount) {
            url += `?fbmAccount=${fbmAccount}`;
        }
        const filename = `order_ingest_results.csv`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
            }
        } catch (e) {
            logError(e);
        }
    },
    getASNs: async (fbmAccount) => {
        const url = `api/fbm/v1/asn?fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getASNDetails: async (id, fbmAccount) => {
        const url = `api/fbm/v1/asn/${id}?fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getFBMAccounts: async (
        accountName = '',
        fromReportScreen = false,
        filterNonFBMAccounts = false,
        filterNonFCFBMAccounts = false,
        facilityID = null,
    ) => {
        let url = `api/fbm/v1/accounts?fromReportScreen=${fromReportScreen}&filterNonFBMAccounts=${filterNonFBMAccounts}&filterNonFCFBMAccounts=${filterNonFCFBMAccounts}`;
        if (accountName) {
            url += `&name=${accountName}`;
        }
        if (facilityID !== null && facilityID !== undefined) {
            url += `&facilityID=${facilityID}`;
        }
        return mmFetchWithResponse(url);
    },
    updateFBMAccountConfig: async (accountId, values) => {
        const url = `api/fbm/v1/accounts/${accountId}/config`;

        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(values),
        });

        return response;
    },
    getHospitalShipments: async (facilityID, fbmAccount) => {
        const url = `api/fbm/v1/shipments/hospital?facilityID=${facilityID}&fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getBulkOrderIngestionDetails: async (fbmAccount) => {
        const url = `api/fbm/v1/orders/bulkIngestDetails?fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getInventory: async (facilityID, fbmAccount) => {
        const url = `api/fbm/v1/inventory?facilityID=${facilityID}&fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getInventoryUnitsandLotDetails: async (facilityID, fbmAccount, externalSkus, packagingIds) => {
        const url = `api/fbm/v1/inventory/units?facilityID=${facilityID}&fbmAccount=${fbmAccount}`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({
                externalSkus,
                packagingIds,
            }),
        });
    },
    updateItemInventoryRiskThreshold: async (
        fbmAccountName,
        thirdPartyItemID,
        inventoryRiskThreshold,
        facilityID,
        type,
    ) => {
        const url = 'api/fbm/v1/inventory';
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({
                inventoryRiskThreshold,
                thirdPartyItemID,
                fbmAccountName,
                facilityID,
                type,
            }),
        });
        return response;
    },
    getItems: async (fbmAccount = '') => {
        let url = `api/fbm/v1/items`;
        if (fbmAccount) {
            url += `?fbmAccount=${fbmAccount}`;
        }
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getViewAllOrders: async (fbmAccount, facilityID) => {
        const url = `api/fbm/v1/orders?fbmAccount=${fbmAccount}&facilityID=${facilityID}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getOrderDetails: async (orderNum, fbmAccount) => {
        const url = `api/fbm/v1/orders/details?orderNum=${encodeURIComponent(orderNum)}&fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getOrderPackagingData: async (fbmAccount, reportStartDate, reportEndDate) => {
        const url = `api/fbm/v1/reports/order-packaging-data/view?fbmAccount=${fbmAccount}&reportStartDate=${reportStartDate}&reportEndDate=${reportEndDate}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPackagingTypes: async (fbmAccount) => mmFetchWithResponse(`api/fbm/v1/packaging/types?fbmAccount=${fbmAccount}`),
    getPackaging: async (fbmAccount) => {
        const url = `api/fbm/v1/packaging?fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    createPackaging: async (packagingParams, fbmAccount) => {
        const params = { ...packagingParams, fbmAccountName: fbmAccount };
        return mmFetchWithResponse(`api/fbm/v1/packaging`, {
            method: 'POST',
            body: JSON.stringify(params),
        });
    },
    getPackagingASNs: async (fbmAccount) => {
        const url = `api/fbm/v1/packagingASN?fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPackagingASNDetails: async (asnID, fbmAccount) => {
        const url = `api/fbm/v1/packagingASN/${asnID}?fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getUserAccounts: async (fbmAccount) => {
        const url = `api/fbm/v1/users/accounts?fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    createFBMUserAccount: async (sendParams) => {
        const url = `api/fbm/v1/users/accounts/create`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    deleteFBMUserAccount: async (sendParams) => {
        const url = `api/fbm/v1/users/accounts/delete`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    resetFBMUserPassword: async (authUserID) => {
        const url = `api/fbm/v1/users/accounts/reset-password`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ authUserID }),
        });
        return response;
    },
    ingestSingleOrder: async (orderID, fbmAccount) => {
        const url = `api/fbm/v1/orders/ingestSingle`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ orderID, fbmAccount }),
        });
        return response;
    },
    getShipmentStatus: async (facilityID, fbmAccount) => {
        const url = `api/fbm/v1/shipments/status?facilityID=${facilityID}&fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    markOrderShipped: async (mmShipID, fbmAccount) => {
        const url = `api/fbm/v1/orders/markShipped`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({
                shipmentID: mmShipID,
                fbmAccount,
            }),
        });
        return response;
    },
    modifyASN: async (asnID, items) => {
        const url = `api/fbm/v1/asn/${asnID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(items),
        });
        return response;
    },
    modifyASNItem: async (asnID, rowID, items) => {
        const url = `api/fbm/v1/asn/${asnID}/items/${rowID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(items),
        });
        return response;
    },
    modifyPackagingASN: async (asnID, items) => {
        const url = `api/fbm/v1/packagingASN/${asnID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(items),
        });
        return response;
    },
    modifyPackagingASNItem: async (asnID, rowID, items) => {
        const url = `api/fbm/v1/packagingASN/${asnID}/items/${rowID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(items),
        });
        return response;
    },
    purchaseShipment: async (id, fbmAccount) => {
        const url = `api/fbm/v1/shipments/retry`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({
                shipmentID: id,
                fbmAccount,
            }),
        });
        return response;
    },
    removeASNItem: async (asnID, rowID, fbmAccount) => {
        const url = `api/fbm/v1/asn/${asnID}/items/${rowID}/remove?fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    removePackagingASNItem: async (asnID, rowID, fbmAccount) => {
        const url = `api/fbm/v1/packagingASN/${asnID}/items/${rowID}/remove?fbmAccount=${fbmAccount}`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },

    // Reports
    getReportReceivingDiscrepancyView: async (fbmAccount, reportStartDate, reportEndDate) => {
        const url = `api/fbm/v1/reports/receiving-discrepancy/view?fbmAccount=${fbmAccount}&reportStartDate=${reportStartDate}&reportEndDate=${reportEndDate}`;
        return mmFetchWithResponse(url);
    },
    getReportPackagingInventoryView: async (fbmAccount) => {
        const url = `api/fbm/v1/reports/packaging-inventory/view?fbmAccount=${fbmAccount}`;
        return mmFetchWithResponse(url);
    },
    getReportOnTimeDeliveryView: async (fbmAccount, reportStartDate, reportEndDate) => {
        const url = `api/fbm/v1/reports/on-time-delivery/view?fbmAccount=${fbmAccount}&reportStartDate=${reportStartDate}&reportEndDate=${reportEndDate}`;
        return mmFetchWithResponse(url);
    },
    getReportExternalSkuUsageView: async (fbmAccount, facilityID) => {
        const url = `api/fbm/v1/reports/external-sku-usage/view?fbmAccount=${fbmAccount}&facilityID=${facilityID}`;
        return mmFetchWithResponse(url);
    },
    getReportDailySkuPackView: async (fbmAccount, reportStartDate, reportEndDate, facilityID) => {
        const url = `api/fbm/v1/reports/daily-sku-pack/view?fbmAccount=${fbmAccount}&facilityID=${facilityID}&reportStartDate=${reportStartDate}&reportEndDate=${reportEndDate}`;
        return mmFetchWithResponse(url);
    },
    // Little spoon specific endpoints
    createSub: async (sendParams) => {
        const url = `api/fbm/v1/littleSpoon/substitutions`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    removeSub: async (rowID) => {
        const url = `api/fbm/v1/littleSpoon/substitutions/${rowID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: '',
        });
        return response;
    },
    getSubList: async (week, year, facilityID) => {
        const url = `api/fbm/v1/littleSpoon/substitutions?facilityID=${facilityID}&week=${week}&year=${year}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
};

export { fbmApi };
